import { Component, type ErrorInfo, type ReactNode } from "react";
import { useCmtsSelector } from "@store/hooks.js";
import { selectCmtsUi } from "@store/selectors.js";
import { logCmtsSafeError, normalizeCmtsApiError } from "@cmts/errors/index.js";
import { SwotRetryPanel } from "@swot-shared/feedback/index.js";
import { ApiErrorPanel } from "./ApiErrorPanel.js";

type AppErrorBoundaryState = {
  readonly error: unknown | null;
};

type AppErrorBoundaryCoreProps = {
  readonly children: ReactNode;
  readonly correlationId?: string | undefined;
};

class AppErrorBoundaryCore extends Component<AppErrorBoundaryCoreProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    logCmtsSafeError(error, { operation: "app-render" });
  }

  private readonly recover = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      const normalized = normalizeCmtsApiError(this.state.error, { operation: "app-render" });
      return (
        <main className="cmts-app-error-boundary" role="main">
          <SwotRetryPanel title="Application recovery available" message="Try to recover this screen, or reload the application if the issue remains." onRetry={this.recover} />
          <ApiErrorPanel error={{ ...normalized, ...(this.props.correlationId ? { correlationId: this.props.correlationId } : {}) }} title="CMTS application runtime issue" onRetry={this.recover} onRefresh={() => window.location.reload()} />
        </main>
      );
    }
    return this.props.children;
  }
}

export function AppErrorBoundary({ children }: { readonly children: ReactNode }) {
  const correlationId = useCmtsSelector((state) => selectCmtsUi(state).lastCorrelationId);
  return <AppErrorBoundaryCore correlationId={correlationId}>{children}</AppErrorBoundaryCore>;
}

function sanitizeComponentStack(stack: string): string {
  return stack.split("\n").slice(0, 8).join("\n");
}
